import React, { useCallback, useEffect, useRef, useState } from "react";
import { PenLine, Brain, MessageSquareText, Loader2, HelpCircle, Check, ArrowRight, BookOpen, GitBranch } from "lucide-react";
import { fscSeed, fscState, fscWriting, fscRecruit, fscTurn } from "@/lib/api";
import { otNoPaste } from "@/lib/otPasteGuard";

const STORE = "fsc_session";

// Functionally Scaffolded Composition — a SINGLE developing writing space (no separate FO artifact).
// The thesis lives inside the editor with usable space before and after; My Thinking stays available;
// TWS scaffolds the smallest consequential communicative work that remains.
export default function FunctionallyScaffoldedComposition({ sessionId: sidProp, onContinueToComposition }) {
  const [sid, setSid] = useState(sidProp || null);
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState("");
  const [thinking, setThinking] = useState({ items: [], groups: [], relationships: [] });
  const [before, setBefore] = useState("");
  const [thesis, setThesis] = useState("");
  const [after, setAfter] = useState("");
  const [region, setRegion] = useState("before");
  const [intention, setIntention] = useState("");
  const [recruited, setRecruited] = useState([]);
  const [tws, setTws] = useState({ message: "", level: "S0", routing: "" });
  const [activeTarget, setActiveTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [thinkingOpen, setThinkingOpen] = useState(false); // reopen focus (S5/route)
  const explainRef = useRef(null);
  const saveTimer = useRef(null);

  // boot: ensure a session, load state
  useEffect(() => {
    (async () => {
      let id = sidProp || localStorage.getItem(STORE);
      if (!id) { const r = await fscSeed(); id = r.session_id; localStorage.setItem(STORE, id); }
      setSid(id);
      const s = await fscState(id);
      setAssignment(s.assignment || "");
      setThinking(s.thinking || { items: [], groups: [], relationships: [] });
      const w = s.fsc?.writing_state || {};
      setBefore(w.before || ""); setThesis(w.thesis || ""); setAfter(w.after || "");
      setRecruited((s.fsc?.recruited || []).flatMap((r) => r.ids || []));
      setLoading(false);
      // first orientation turn
      runTurn(id, { intention: "" }, w);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = useCallback((next) => {
    if (!sid) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { fscWriting(sid, next).catch(() => {}); }, 500);
  }, [sid]);

  const onEdit = (which, val) => {
    const next = { before, thesis, after, active_region: region };
    if (which === "before") { setBefore(val); next.before = val; next.active_region = "before"; setRegion("before"); }
    if (which === "thesis") { setThesis(val); next.thesis = val; next.active_region = "thesis"; setRegion("thesis"); }
    if (which === "after") { setAfter(val); next.after = val; next.active_region = "after"; setRegion("after"); }
    persist(next);
  };

  const runTurn = useCallback(async (id, body, wOverride) => {
    setBusy(true);
    try {
      const w = wOverride || { before, thesis, after, active_region: region };
      await fscWriting(id, w).catch(() => {});
      const r = await fscTurn(id, body);
      setTws({ message: r.message, level: r.support_level, routing: r.routing });
      setActiveTarget(r.active_target || null);
      if (r.routing === "reopen_thinking") setThinkingOpen(true);
    } finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [before, thesis, after, region]);

  const toggleRecruit = async (item) => {
    const on = recruited.includes(item.id);
    const nextIds = on ? recruited.filter((x) => x !== item.id) : [...recruited, item.id];
    setRecruited(nextIds);
    if (sid) fscRecruit(sid, nextIds, "idea").catch(() => {});
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#faf9f6]"><Loader2 className="h-6 w-6 animate-spin text-[#8C3A2A]" /></div>;

  const groupItems = (gid) => thinking.items.filter((it) => (it.group_ids || [it.group_id]).includes(gid));
  const ungrouped = thinking.items.filter((it) => !(it.group_ids && it.group_ids.length) && !it.group_id);

  // Real OT hierarchy (questions + nested ideas) — the student's actual representation, VERBATIM.
  const questions = (thinking.questions || []).filter((q) => (q.source || "student") === "student");
  const qrels = thinking.question_relationships || [];
  const qById = (id) => questions.find((q) => q.id === id);
  const childQIds = (qid) => qrels.filter((r) => r.to_id === qid).map((r) => r.from_id).filter((id) => qById(id));
  const isChildQ = (qid) => qrels.some((r) => r.from_id === qid && qById(r.to_id));
  const rootQs = questions.filter((q) => !isChildQ(q.id) && !q.parent_item_id && !q.unplaced);
  const primaryQ = (i) => (i.question_ids || []).find((qid) => qById(qid));
  const directIdeasFor = (qid) => thinking.items.filter((i) => !i.parent_id && primaryQ(i) === qid);
  const childIdeasOf = (id) => thinking.items.filter((i) => i.parent_id === id);
  const childQuestionsOfIdea = (id) => questions.filter((q) => q.parent_item_id === id);
  const looseIdeas = thinking.items.filter((i) => !i.parent_id && !primaryQ(i));
  const hasHierarchy = questions.length > 0;

  // iteration_75 continuity: a parent's children (questions AND ideas) render in ONE position-sorted
  // order — never flattened into "questions then ideas". Verbatim; the student's deliberate order is kept.
  const cmpSib = (a, b) => {
    const pa = typeof a.position === "number" ? a.position : null;
    const pb = typeof b.position === "number" ? b.position : null;
    if (pa !== null && pb !== null) return pa - pb;
    if (pa !== null) return -1;
    if (pb !== null) return 1;
    return String(a.created_at || "").localeCompare(String(b.created_at || ""));
  };
  const mergedQChildren = (qid) => [
    ...childQIds(qid).map((id) => ({ kind: "q", node: qById(id) })).filter((x) => x.node),
    ...directIdeasFor(qid).map((n) => ({ kind: "i", node: n })),
  ].sort((a, b) => cmpSib(a.node, b.node));
  const mergedIdeaChildren = (id) => [
    ...childIdeasOf(id).map((n) => ({ kind: "i", node: n })),
    ...childQuestionsOfIdea(id).map((n) => ({ kind: "q", node: n })),
  ].sort((a, b) => cmpSib(a.node, b.node));
  const rootIdeasWithChildren = looseIdeas.filter((i) => childIdeasOf(i.id).length > 0);
  const looseTray = looseIdeas.filter((i) => childIdeasOf(i.id).length === 0);
  const mergedRoots = () => [
    ...rootQs.map((n) => ({ kind: "q", node: n })),
    ...rootIdeasWithChildren.map((n) => ({ kind: "i", node: n })),
  ].sort((a, b) => cmpSib(a.node, b.node));
  const renderNode = (c, depth) => (c.kind === "q" ? QNode(c.node, depth) : IdeaBtn(c.node, depth));

  const bringIntoWriting = (item, target = "after") => {
    const text = (item.text || "").trim();
    if (!text) return;
    // COPY FORWARD verbatim; never consume the source. Student chooses BEFORE or AFTER the thesis.
    if (target === "before") {
      const cur = (before || "").replace(/\s+$/, "");
      onEdit("before", cur ? `${cur}\n\n${text}` : text);
      setRegion("before");
    } else {
      const cur = (after || "").replace(/\s+$/, "");
      onEdit("after", cur ? `${cur}\n\n${text}` : text);
      setRegion("after");
    }
    const nextIds = recruited.includes(item.id) ? recruited : [...recruited, item.id];
    setRecruited(nextIds);
    if (sid) fscRecruit(sid, nextIds, "idea").catch(() => {});   // provenance link (student-authored → writing)
  };
  const eligible = (it) => ((it.source || "student") === "student");

  const IdeaBtn = (it, depth = 0) => (
    <div key={it.id} style={depth ? { marginLeft: 12 } : undefined} className="mb-1.5">
      <div className={`text-[13px] leading-snug px-3 py-2 rounded-md border ${recruited.includes(it.id) ? "border-[#8C3A2A] bg-[#8C3A2A]/[0.06] text-stone-900" : "border-stone-200 bg-white text-stone-700"}`}>
        <button data-testid={`fsc-idea-${it.id}`} onClick={() => toggleRecruit(it)} className="w-full text-left">
          <span className="inline-flex items-start gap-1.5">
            {recruited.includes(it.id) ? <Check className="h-3 w-3 text-[#8C3A2A] mt-0.5 shrink-0" /> : <BookOpen className="h-3 w-3 text-stone-300 mt-0.5 shrink-0" />}
            <span>{it.text}</span>
          </span>
        </button>
        {eligible(it) && (
          <div className="mt-2 pt-2 border-t border-stone-100">
            <p className="text-[9px] font-mono uppercase tracking-[0.16em] text-stone-400 mb-1.5">Bring into writing</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <button data-testid={`fsc-bring-before-${it.id}`} onClick={() => bringIntoWriting(it, "before")}
                className="inline-flex items-center gap-1 text-[11.5px] font-medium text-[#8C3A2A] border border-[#8C3A2A]/40 rounded-full px-2.5 py-1 hover:bg-[#8C3A2A]/10 transition-colors">
                Before thesis
              </button>
              <button data-testid={`fsc-bring-after-${it.id}`} onClick={() => bringIntoWriting(it, "after")}
                className="inline-flex items-center gap-1 text-[11.5px] font-medium text-[#8C3A2A] border border-[#8C3A2A]/40 rounded-full px-2.5 py-1 hover:bg-[#8C3A2A]/10 transition-colors">
                After thesis
              </button>
            </div>
          </div>
        )}
      </div>
      {mergedIdeaChildren(it.id).map((c) => renderNode(c, depth + 1))}
    </div>
  );
  const QNode = (q, depth = 0) => {
    if (!q) return null;
    return (
      <div key={q.id} data-testid={`fsc-question-${q.id}`} style={depth ? { marginLeft: 12 } : undefined} className="space-y-1.5">
        <p className="text-[12.5px] font-semibold text-[#8C3A2A] leading-snug flex items-start gap-1.5">
          <GitBranch className="h-3.5 w-3.5 mt-0.5 shrink-0" />{q.text}
        </p>
        <div className="space-y-1.5 ml-3">
          {mergedQChildren(q.id).map((c) => renderNode(c, 0))}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="fsc-root" className="min-h-screen bg-[#faf9f6] text-stone-900">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-5">
        <div className="mb-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A]">The Writing Studio · Composition</p>
          <p data-testid="fsc-assignment" className="text-[13px] text-stone-600 mt-1 max-w-3xl">{assignment}</p>
        </div>

        {/* DOMINANT, AUTO-SURFACING HANDOFF — appears the moment the draft is functionally sufficient,
            at the top of the page (no scroll needed) as the single most prominent action. */}
        {tws.routing === "whole_complete" && (
          <div data-testid="fsc-continue-banner" className="mb-5 rounded-2xl border-2 border-emerald-500 bg-emerald-50 p-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-700">Functional draft complete</p>
              <p className="text-[15px] font-medium text-emerald-900 mt-1">Your draft is organized well enough to keep developing the writing.</p>
            </div>
            <button data-testid="continue-to-composition-top" disabled={busy}
              onClick={() => onContinueToComposition && onContinueToComposition(`${(before || "").trim()}\n\n${(thesis || "").trim()}\n\n${(after || "").trim()}`.trim())}
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 text-white text-[15px] font-semibold px-6 py-3 rounded-lg hover:bg-emerald-700 enabled:hover:-translate-y-px transition-[background-color,transform] disabled:opacity-40 shrink-0">
              Continue to Composition <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-5">
          {/* MY WRITING — thesis lives INSIDE the editor with space before & after */}
          <section data-testid="fsc-writing" className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <PenLine className="h-4 w-4 text-[#8C3A2A]" />
              <h2 className="font-serif text-lg text-stone-900">My Writing</h2>
            </div>
            <textarea data-testid="fsc-before" {...otNoPaste} value={before} onFocus={() => setRegion("before")}
              onChange={(e) => onEdit("before", e.target.value)} placeholder="Write what your reader needs before your thesis…"
              className="w-full min-h-[130px] text-[15px] leading-relaxed p-3 rounded-lg border border-stone-200 outline-none focus:border-[#8C3A2A] resize-y" />
            <div className="my-2 rounded-lg border-2 border-[#8C3A2A]/25 bg-[#8C3A2A]/[0.03] p-3">
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8C3A2A]/80 mb-1">Working thesis — provisional</p>
              <textarea data-testid="fsc-thesis" {...otNoPaste} value={thesis} onFocus={() => setRegion("thesis")}
                onChange={(e) => onEdit("thesis", e.target.value)}
                className="w-full min-h-[64px] text-[15px] leading-relaxed bg-transparent outline-none resize-y" />
              <p className="text-[10px] text-[#8C3A2A]/60 mt-1">Your organizing idea — it stays editable and may change as your writing develops.</p>
            </div>
            <textarea data-testid="fsc-after" {...otNoPaste} value={after} onFocus={() => setRegion("after")}
              onChange={(e) => onEdit("after", e.target.value)} placeholder="Continue your writing here…"
              className="w-full min-h-[220px] text-[15px] leading-relaxed p-3 rounded-lg border border-stone-200 outline-none focus:border-[#8C3A2A] resize-y" />
            {/* Bottom continuation: same instructional state as TWS, at the point of work. No second chat. */}
            {(after || before) && (
              <div data-testid="fsc-what-next" className="mt-4 rounded-xl border border-stone-200 bg-stone-50/70 p-4">
                <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-400 mb-1">What next?</p>
                {tws.routing === "whole_review" ? (
                  <p className="text-[12.5px] text-stone-600 mb-3">The Writing Studio thinks the piece may be complete. You can review the whole draft, or keep developing it.</p>
                ) : (
                  <p className="text-[12.5px] text-stone-600 mb-3">Keep developing this part, check whether it now works, or review the whole draft if you think you're finished.</p>
                )}
                {tws.routing === "whole_complete" ? (
                  <div data-testid="fsc-draft-complete">
                    <p className="text-[12.5px] text-emerald-800 mb-3"><span className="font-semibold">Functional draft complete.</span> Your draft is organized well enough to continue developing the writing.</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <button data-testid="continue-to-composition" onClick={() => onContinueToComposition && onContinueToComposition(`${(before || "").trim()}\n\n${(thesis || "").trim()}\n\n${(after || "").trim()}`.trim())} disabled={busy}
                        className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[13px] font-medium px-4 py-2.5 rounded-md hover:bg-emerald-700 disabled:opacity-40">
                        Continue to Composition <ArrowRight className="h-4 w-4" />
                      </button>
                      <button data-testid="wn-keep" onClick={() => runTurn(sid, { intention, mode: "recheck" })} disabled={busy}
                        className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-500 hover:text-[#8C3A2A] disabled:opacity-40">Keep developing my writing</button>
                      <button data-testid="wn-help" onClick={() => runTurn(sid, { intention, help_signal: true, mode: "help" })} disabled={busy}
                        className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-500 hover:text-[#8C3A2A] disabled:opacity-40"><HelpCircle className="h-3.5 w-3.5" /> Help me here</button>
                    </div>
                  </div>
                ) : tws.routing === "whole_review" ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <button data-testid="wn-whole" onClick={() => runTurn(sid, { intention, mode: "whole_review" })} disabled={busy}
                      className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[12.5px] font-medium px-3.5 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-40"><Check className="h-3.5 w-3.5" /> Review my whole draft</button>
                    <button data-testid="wn-keep" onClick={() => runTurn(sid, { intention, mode: "recheck" })} disabled={busy}
                      className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-500 hover:text-[#8C3A2A] disabled:opacity-40">Keep developing my writing</button>
                    <button data-testid="wn-help" onClick={() => runTurn(sid, { intention, help_signal: true, mode: "help" })} disabled={busy}
                      className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-500 hover:text-[#8C3A2A] disabled:opacity-40"><HelpCircle className="h-3.5 w-3.5" /> Help me here</button>
                  </div>
                ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <button data-testid="wn-check" onClick={() => runTurn(sid, { intention, mode: "recheck" })} disabled={busy}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3.5 py-2 rounded-md border border-[#8C3A2A]/40 text-[#8C3A2A] hover:bg-[#8C3A2A]/5 disabled:opacity-40">
                    <Check className="h-3.5 w-3.5" /> Check this part
                  </button>
                  <button data-testid="wn-ready" onClick={() => runTurn(sid, { intention, mode: "ready" })} disabled={busy}
                    className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[12.5px] font-medium px-3.5 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-40">
                    <ArrowRight className="h-3.5 w-3.5" /> I'm ready to continue
                  </button>
                  <button data-testid="wn-whole" onClick={() => runTurn(sid, { intention, mode: "whole_review" })} disabled={busy}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3.5 py-2 rounded-md border border-stone-300 text-stone-600 hover:text-stone-900 disabled:opacity-40">
                    <Check className="h-3.5 w-3.5" /> Review my whole draft
                  </button>
                  <button data-testid="wn-help" onClick={() => runTurn(sid, { intention, help_signal: true, mode: "help" })} disabled={busy}
                    className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-500 hover:text-[#8C3A2A] disabled:opacity-40">
                    <HelpCircle className="h-3.5 w-3.5" /> Help me here
                  </button>
                </div>
                )}
              </div>
            )}
          </section>

          {/* RIGHT: TWS + MY THINKING */}
          <div className="space-y-5">
            <section data-testid="fsc-tws" className="rounded-2xl border border-stone-200 bg-white p-5">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquareText className="h-4 w-4 text-[#8C3A2A]" />
                <h2 className="font-serif text-lg text-stone-900">The Writing Studio</h2>
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-stone-400 ml-auto" />}
                {!busy && tws.level !== "S0" && <span data-testid="fsc-support-level" className="ml-auto text-[9px] font-mono uppercase tracking-wider text-stone-300">{tws.level}</span>}
              </div>
              <div data-testid="fsc-current-turn" className="rounded-lg border border-[#8C3A2A]/25 bg-[#8C3A2A]/[0.04] px-3.5 py-2.5">
                <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8C3A2A]/80 mb-0.5">Current work</p>
                <p data-testid="fsc-tws-message" className="text-[14px] font-medium leading-relaxed text-stone-900 min-h-[40px]">
                  {tws.message || "Look at your thesis and the thinking you've developed. What does your reader need to understand before encountering your thesis?"}
                </p>
              </div>
              <div className="mt-3">
                <input data-testid="fsc-intention" ref={explainRef} {...otNoPaste} value={intention}
                  onChange={(e) => setIntention(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && intention.trim() && !busy) runTurn(sid, { explanation: intention.trim(), mode: "explain" }); }}
                  placeholder="Explain your thinking, or say what you're doing… (Enter to send)"
                  className="w-full text-[13px] px-3 py-2 rounded-md border border-stone-200 outline-none focus:border-[#8C3A2A]" />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button data-testid="fsc-recheck" onClick={() => runTurn(sid, { intention, mode: "recheck" })} disabled={busy}
                  className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[12.5px] font-medium px-3.5 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-40">
                  <Check className="h-3.5 w-3.5" /> Recheck my writing
                </button>
                <button data-testid="fsc-help" onClick={() => runTurn(sid, { intention, help_signal: true, mode: "help" })} disabled={busy}
                  className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-600 hover:text-[#8C3A2A] disabled:opacity-40">
                  <HelpCircle className="h-3.5 w-3.5" /> Help me here
                </button>
                <button data-testid="fsc-explain" onClick={() => { if (intention.trim()) runTurn(sid, { explanation: intention.trim(), mode: "explain" }); else explainRef.current?.focus(); }} disabled={busy}
                  className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-600 hover:text-[#8C3A2A] disabled:opacity-40">
                  <MessageSquareText className="h-3.5 w-3.5" /> Let me explain
                </button>
                {tws.routing === "whole_review" && (
                  <span data-testid="fsc-whole-review" className="inline-flex items-center gap-1 text-[12px] text-emerald-700 ml-auto"><ArrowRight className="h-3.5 w-3.5" /> Ready for whole-piece review</span>
                )}
              </div>
            </section>

            <section data-testid="fsc-thinking" className={`rounded-2xl border p-5 transition-colors ${thinkingOpen ? "border-[#8C3A2A]/40 bg-[#8C3A2A]/[0.03]" : "border-stone-200 bg-white"}`}>
              <div className="flex items-center gap-2 mb-1">
                <Brain className="h-4 w-4 text-[#8C3A2A]" />
                <h2 className="font-serif text-lg text-stone-900">My Thinking</h2>
                {thinkingOpen && <span className="ml-auto text-[10px] uppercase tracking-wider text-[#8C3A2A]">have a look here</span>}
              </div>
              <p className="text-[11px] text-stone-500 mb-3">These are the ideas you worked out earlier. Use them as a reference, or <span className="font-medium text-[#8C3A2A]">bring your own words into your writing</span> and keep working with them there.</p>
              <div className="space-y-3 max-h-[46vh] overflow-y-auto pr-1">
                {hasHierarchy ? (
                  <>
                    {mergedRoots().map((c) => renderNode(c, 0))}
                    {looseTray.length > 0 && (
                      <div className="space-y-1.5 pt-1">
                        <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-stone-400">Not placed yet</p>
                        {looseTray.map((i) => IdeaBtn(i, 0))}
                      </div>
                    )}
                    {mergedRoots().length === 0 && looseTray.length === 0 && (
                      <p data-testid="fsc-thinking-empty" className="text-[12px] text-stone-400 italic">Your organized thinking will appear here.</p>
                    )}
                  </>
                ) : (
                  <>
                {thinking.groups.map((g) => (
                  <div key={g.id} data-testid={`fsc-group-${g.id}`}>
                    <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-stone-400 mb-1.5">{g.label || g.title}</p>
                    <div className="space-y-1.5">
                      {groupItems(g.id).map((it) => (
                        <button key={it.id} data-testid={`fsc-idea-${it.id}`} onClick={() => toggleRecruit(it)}
                          className={`w-full text-left text-[13px] leading-snug px-3 py-2 rounded-md border transition-colors ${recruited.includes(it.id) ? "border-[#8C3A2A] bg-[#8C3A2A]/[0.06] text-stone-900" : "border-stone-200 bg-white text-stone-700 hover:border-stone-300"}`}>
                          <span className="inline-flex items-center gap-1.5">
                            {recruited.includes(it.id) ? <Check className="h-3 w-3 text-[#8C3A2A]" /> : <BookOpen className="h-3 w-3 text-stone-300" />}
                            {it.text}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {ungrouped.length > 0 && (
                  <div className="space-y-1.5">
                    {ungrouped.map((it) => (
                      <button key={it.id} data-testid={`fsc-idea-${it.id}`} onClick={() => toggleRecruit(it)}
                        className={`w-full text-left text-[13px] leading-snug px-3 py-2 rounded-md border ${recruited.includes(it.id) ? "border-[#8C3A2A] bg-[#8C3A2A]/[0.06]" : "border-stone-200 bg-white hover:border-stone-300"}`}>
                        {it.text}
                      </button>
                    ))}
                  </div>
                )}
                  </>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
